import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_BASE_URL, API_KEY, getApiHeaders } from '../config/api';

const walletApiBase = `${API_BASE_URL.replace(/\/$/, '')}/wallet/t`;

type UploadState = 'idle' | 'uploading' | 'done' | 'error';

type LottieSlot = {
  assetType: 'gift' | 'background' | 'preview';
  label: string;
  required: boolean;
  file: File | null;
  previewName: string;
  previewData: any | null;
  uploadState: UploadState;
  uploadedUrl: string | null;
  error: string | null;
};

type AudioSlot = {
  file: File | null;
  uploadState: UploadState;
  uploadedUrl: string | null;
  error: string | null;
};

type WebmSlot = {
  file: File | null;
  uploadState: UploadState;
  uploadedUrl: string | null;
  error: string | null;
};

type MovSlot = {
  file: File | null;
  uploadState: UploadState;
  uploadedUrl: string | null;
  error: string | null;
};

const ACCEPTED = '.json';

const loadLottie = (): Promise<any> =>
  new Promise((resolve) => {
    const win = window as any;
    if (win.lottie) return resolve(win.lottie);
    const existing = document.querySelector('script[data-lottie]');
    if (existing) {
      const interval = setInterval(() => {
        if (win.lottie) { clearInterval(interval); resolve(win.lottie); }
      }, 100);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
    script.setAttribute('data-lottie', '1');
    script.onload = () => resolve(win.lottie);
    document.head.appendChild(script);
  });

const LottiePreview: React.FC<{ data: any }> = ({ data }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    loadLottie().then((lottie) => {
      if (cancelled || !containerRef.current) return;
      animRef.current?.destroy();
      animRef.current = lottie.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        animationData: data,
      });
    });
    return () => {
      cancelled = true;
      animRef.current?.destroy();
    };
  }, [data]);

  return <div ref={containerRef} style={{ width: 140, height: 140 }} />;
};

const fileNameToKey = (filename: string): string =>
  filename
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .toUpperCase();

const fileNameToLabel = (filename: string): string => {
  const base = filename.replace(/\.[^/.]+$/, '');
  return base
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const CreateEventGift: React.FC = () => {
  const navigate = useNavigate();

  const [productKey, setProductKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priceTokens, setPriceTokens] = useState('50');
  const [displayOrder, setDisplayOrder] = useState('1');
  const [availabilityType, setAvailabilityType] = useState<'unlimited' | 'limited'>('unlimited');
  const [environment, setEnvironment] = useState<'dev' | 'prod'>('prod');

  const [submitting, setSubmitting] = useState(false);
  const [audioSlot, setAudioSlot] = useState<AudioSlot>({
    file: null,
    uploadState: 'idle',
    uploadedUrl: null,
    error: null,
  });

  const [webmSlot, setWebmSlot] = useState<WebmSlot>({
    file: null,
    uploadState: 'idle',
    uploadedUrl: null,
    error: null,
  });

  const [movSlot, setMovSlot] = useState<MovSlot>({
    file: null,
    uploadState: 'idle',
    uploadedUrl: null,
    error: null,
  });

  const [slots, setSlots] = useState<LottieSlot[]>([
    {
      assetType: 'gift',
      label: 'Gift Animation (Lottie JSON)',
      required: true,
      file: null,
      previewName: '',
      previewData: null,
      uploadState: 'idle',
      uploadedUrl: null,
      error: null,
    },
    {
      assetType: 'preview',
      label: 'Preview Animation (Lottie JSON)',
      required: false,
      file: null,
      previewName: '',
      previewData: null,
      uploadState: 'idle',
      uploadedUrl: null,
      error: null,
    },
    {
      assetType: 'background',
      label: 'Background Animation (Lottie JSON)',
      required: false,
      file: null,
      previewName: '',
      previewData: null,
      uploadState: 'idle',
      uploadedUrl: null,
      error: null,
    },
  ]);

  // Auto-fill max DisplayOrder from existing event gifts
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token || !API_KEY) return;
    fetch(`${walletApiBase}/getWalletShopProductsV2`, {
      method: 'POST',
      headers: getApiHeaders(token),
      body: JSON.stringify({ UserID: 'dashboard', CategoryKey: 'EVENT_GIFT', CurrencyType: 'token', IncludeInactive: true }),
    })
      .then((r) => r.json())
      .then((json) => {
        const products: { DisplayOrder?: number }[] = json?.data?.products || [];
        const maxOrder = products.reduce((max, p) => Math.max(max, p.DisplayOrder ?? 0), 0);
        setDisplayOrder(String(maxOrder + 1));
      })
      .catch(() => {});
  }, []);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) { toast.error('No token found. Please log in.'); return null; }
    if (!API_KEY) { toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.'); return null; }
    return getApiHeaders(token);
  }, []);

  const handleFileSelect = (slotType: LottieSlot['assetType'], e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target?.result as string);
        setSlots((prev) =>
          prev.map((s) =>
            s.assetType === slotType
              ? { ...s, file, previewName: file.name, previewData: data, uploadState: 'idle', uploadedUrl: null, error: null }
              : s
          )
        );
        if (slotType === 'gift' && !productKey) {
          setProductKey(fileNameToKey(file.name));
          setName(fileNameToLabel(file.name));
        }
      } catch {
        toast.error('Invalid JSON file — could not parse Lottie data.');
      }
    };
    reader.readAsText(file);
  };

  const handleAudioSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAudioSlot({ file, uploadState: 'idle', uploadedUrl: null, error: null });
  };

  const uploadAudio = async (headers: Record<string, string>): Promise<string> => {
    if (!audioSlot.file) throw new Error('No audio file');
    setAudioSlot((prev) => ({ ...prev, uploadState: 'uploading', error: null }));

    const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ProductKey: productKey.trim(),
        ContentType: audioSlot.file.type || 'audio/mpeg',
        AssetType: 'audio',
      }),
    });
    const presignedJson = await presignedRes.json();
    if (!presignedRes.ok) throw new Error(presignedJson.message || 'Failed to get upload URL');

    const { UploadUrl, AssetUrl } = presignedJson.data;
    const uploadRes = await fetch(UploadUrl, {
      method: 'PUT',
      body: await audioSlot.file.arrayBuffer(),
      headers: { 'Content-Type': audioSlot.file.type || 'audio/mpeg' },
    });
    if (!uploadRes.ok) throw new Error('Failed to upload audio file');

    setAudioSlot((prev) => ({ ...prev, uploadState: 'done', uploadedUrl: AssetUrl }));
    return AssetUrl as string;
  };

  const handleWebmSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setWebmSlot({ file, uploadState: 'idle', uploadedUrl: null, error: null });
  };

  const handleMovSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setMovSlot({ file, uploadState: 'idle', uploadedUrl: null, error: null });
  };

  const uploadMov = async (headers: Record<string, string>): Promise<string> => {
    if (!movSlot.file) throw new Error('No MOV file');
    setMovSlot((prev) => ({ ...prev, uploadState: 'uploading', error: null }));

    const contentType = movSlot.file.type || 'video/quicktime';
    const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ProductKey: productKey.trim(),
        ContentType: contentType,
        AssetType: 'ios_alpha',
      }),
    });
    const presignedJson = await presignedRes.json();
    if (!presignedRes.ok) throw new Error(presignedJson.message || 'Failed to get upload URL');

    const { UploadUrl, AssetUrl } = presignedJson.data;
    const uploadRes = await fetch(UploadUrl, {
      method: 'PUT',
      body: await movSlot.file.arrayBuffer(),
      headers: { 'Content-Type': contentType },
    });
    if (!uploadRes.ok) throw new Error('Failed to upload MOV file');

    setMovSlot((prev) => ({ ...prev, uploadState: 'done', uploadedUrl: AssetUrl }));
    return AssetUrl as string;
  };

  const uploadWebm = async (headers: Record<string, string>): Promise<string> => {
    if (!webmSlot.file) throw new Error('No WebM file');
    setWebmSlot((prev) => ({ ...prev, uploadState: 'uploading', error: null }));

    const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ProductKey: productKey.trim(),
        ContentType: 'video/webm',
        AssetType: 'webm',
      }),
    });
    const presignedJson = await presignedRes.json();
    if (!presignedRes.ok) throw new Error(presignedJson.message || 'Failed to get upload URL');

    const { UploadUrl, AssetUrl } = presignedJson.data;
    const uploadRes = await fetch(UploadUrl, {
      method: 'PUT',
      body: await webmSlot.file.arrayBuffer(),
      headers: { 'Content-Type': 'video/webm' },
    });
    if (!uploadRes.ok) throw new Error('Failed to upload WebM file');

    setWebmSlot((prev) => ({ ...prev, uploadState: 'done', uploadedUrl: AssetUrl }));
    return AssetUrl as string;
  };

  const uploadSlot = async (slot: LottieSlot, headers: Record<string, string>): Promise<string> => {
    if (!slot.file) throw new Error('No file');

    setSlots((prev) =>
      prev.map((s) => (s.assetType === slot.assetType ? { ...s, uploadState: 'uploading', error: null } : s))
    );

    const contentType = 'application/json';

    const assetTypeMap: Record<LottieSlot['assetType'], string> = {
      gift: 'animation',
      preview: 'preview',
      background: 'background',
    };

    const presignedRes = await fetch(`${walletApiBase}/createWalletAssetUploadPresignedUrl`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        ProductKey: productKey.trim(),
        ContentType: contentType,
        AssetType: assetTypeMap[slot.assetType],
      }),
    });
    const presignedJson = await presignedRes.json();
    if (!presignedRes.ok) throw new Error(presignedJson.message || 'Failed to get upload URL');

    const { UploadUrl, AssetUrl } = presignedJson.data;

    const uploadRes = await fetch(UploadUrl, {
      method: 'PUT',
      body: await slot.file.arrayBuffer(),
      headers: { 'Content-Type': contentType },
    });
    if (!uploadRes.ok) throw new Error('Failed to upload file to storage');

    setSlots((prev) =>
      prev.map((s) =>
        s.assetType === slot.assetType ? { ...s, uploadState: 'done', uploadedUrl: AssetUrl } : s
      )
    );

    return AssetUrl as string;
  };

  const handleSubmit = async () => {
    const headers = headerWithAuth();
    if (!headers) return;

    const key = productKey.trim();
    const displayName = name.trim();

    if (!key) { toast.error('Product Key is required.'); return; }
    if (!displayName) { toast.error('Name is required.'); return; }

    const price = Number(priceTokens);
    if (isNaN(price) || price <= 0) { toast.error('Price Tokens must be a positive number.'); return; }

    const giftSlot = slots.find((s) => s.assetType === 'gift')!;
    if (!giftSlot.file) { toast.error('Gift animation (.json) is required.'); return; }

    setSubmitting(true);

    try {
      let giftUrl: string;
      if (giftSlot.uploadedUrl) {
        giftUrl = giftSlot.uploadedUrl;
      } else {
        giftUrl = await uploadSlot(giftSlot, headers);
      }

      const previewSlot = slots.find((s) => s.assetType === 'preview')!;
      let previewUrl: string | null = null;
      if (previewSlot.file) {
        previewUrl = previewSlot.uploadedUrl || await uploadSlot(previewSlot, headers);
      }

      const bgSlot = slots.find((s) => s.assetType === 'background')!;
      let bgUrl: string | null = null;
      if (bgSlot.file) {
        bgUrl = bgSlot.uploadedUrl || await uploadSlot(bgSlot, headers);
      }

      let uploadedAudioUrl: string | null = null;
      if (audioSlot.file) {
        uploadedAudioUrl = audioSlot.uploadedUrl || await uploadAudio(headers);
      }

      let uploadedWebmUrl: string | null = null;
      if (webmSlot.file) {
        uploadedWebmUrl = webmSlot.uploadedUrl || await uploadWebm(headers);
      }

      let uploadedMovUrl: string | null = null;
      if (movSlot.file) {
        uploadedMovUrl = movSlot.uploadedUrl || await uploadMov(headers);
      }

      const mediaAssets: { Type: string; Url: string; MimeType: string }[] = [
        { Type: 'animation', Url: giftUrl, MimeType: 'application/json' },
      ];
      if (previewUrl) {
        mediaAssets.push({ Type: 'preview', Url: previewUrl, MimeType: 'application/json' });
      }
      if (bgUrl) {
        mediaAssets.push({ Type: 'background', Url: bgUrl, MimeType: 'application/json' });
      }
      if (uploadedAudioUrl) {
        mediaAssets.push({ Type: 'audio', Url: uploadedAudioUrl, MimeType: audioSlot.file?.type || 'audio/mpeg' });
      }
      if (uploadedWebmUrl) {
        mediaAssets.push({ Type: 'webm', Url: uploadedWebmUrl, MimeType: 'video/webm' });
      }
      if (uploadedMovUrl) {
        mediaAssets.push({ Type: 'ios_alpha', Url: uploadedMovUrl, MimeType: movSlot.file?.type || 'video/quicktime' });
      }

      const productRes = await fetch(`${walletApiBase}/createWalletShopProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ProductKey: key,
          Name: displayName,
          Description: description.trim() || `${displayName} event gift.`,
          CategoryKey: 'EVENT_GIFT',
          Type: 'event_gift',
          CurrencyType: 'token',
          PriceCoins: 0,
          PriceTokens: price,
          AvailabilityType: availabilityType,
          DisplayOrder: Number(displayOrder) || 1,
          Environment: environment,
          IsActive: true,
          MediaAssets: mediaAssets,
        }),
      });
      const productJson = await productRes.json();
      if (!productRes.ok) throw new Error(productJson.message || 'Failed to create product');

      toast.success(`Event gift "${displayName}" created successfully!`);
      navigate('/products?category=GIFT');
    } catch (err) {
      setSlots((prev) =>
        prev.map((s) =>
          s.uploadState === 'uploading'
            ? { ...s, uploadState: 'error', error: (err as Error).message }
            : s
        )
      );
      toast.error((err as Error).message || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  const giftSlot = slots.find((s) => s.assetType === 'gift')!;
  const previewSlot = slots.find((s) => s.assetType === 'preview')!;
  const bgSlot = slots.find((s) => s.assetType === 'background')!;
  const canSubmit = !submitting && !!giftSlot.file && productKey.trim() && name.trim() && Number(priceTokens) > 0;

  const slotStateColor = (state: UploadState) => {
    if (state === 'done') return 'border-emerald-400/40 bg-emerald-500/5';
    if (state === 'error') return 'border-red-400/40 bg-red-500/5';
    if (state === 'uploading') return 'border-amber-400/40 bg-amber-500/5';
    return 'border-white/10 bg-white/5';
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6'>
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Create Event Gift</h1>
            <p className='text-sm text-gray-400 mt-1'>
              Event gifts use Lottie JSON (.json) files. The gift animation is required; the background is optional.
            </p>
          </div>
          <button
            onClick={() => navigate('/products?category=GIFT')}
            className='px-4 py-2 text-sm font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20'
          >
            Back
          </button>
        </div>

        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
          <h2 className='text-lg font-semibold text-white'>Product Details</h2>

          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={submitting}
                placeholder='Golden Throne'
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 disabled:opacity-50'
              />
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Product Key *</label>
              <input
                value={productKey}
                onChange={(e) => setProductKey(e.target.value.replace(/\s+/g, '_').toUpperCase())}
                disabled={submitting}
                placeholder='GOLDEN_THRONE'
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white font-mono placeholder-gray-500 disabled:opacity-50'
              />
            </div>
            <div className='sm:col-span-2'>
              <label className='block text-xs text-gray-400 mb-1'>Description</label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={submitting}
                placeholder='A golden throne drops from the sky.'
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 disabled:opacity-50'
              />
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Price (Tokens) *</label>
              <input
                type='number'
                min='1'
                value={priceTokens}
                onChange={(e) => setPriceTokens(e.target.value)}
                disabled={submitting}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50'
              />
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Display Order</label>
              <input
                type='number'
                min='0'
                value={displayOrder}
                onChange={(e) => setDisplayOrder(e.target.value)}
                disabled={submitting}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50'
              />
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Availability</label>
              <select
                value={availabilityType}
                onChange={(e) => setAvailabilityType(e.target.value as 'unlimited' | 'limited')}
                disabled={submitting}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50'
              >
                <option value='unlimited'>Unlimited</option>
                <option value='limited'>Limited</option>
              </select>
            </div>
            <div>
              <label className='block text-xs text-gray-400 mb-1'>Environment</label>
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as 'dev' | 'prod')}
                disabled={submitting}
                className='w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white disabled:opacity-50'
              >
                <option value='prod'>prod</option>
                <option value='dev'>dev</option>
              </select>
            </div>
          </div>
        </div>

        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 space-y-4'>
          <h2 className='text-lg font-semibold text-white'>Animation Files</h2>
          <p className='text-xs text-gray-400'>
            Both slots accept <span className='font-mono'>.json</span> Lottie animation files only.
          </p>

          {[giftSlot, previewSlot, bgSlot].map((slot) => (
            <div
              key={slot.assetType}
              className={`border rounded-xl p-4 transition space-y-3 ${slotStateColor(slot.uploadState)}`}
            >
              <div className='flex items-start justify-between gap-3'>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 mb-1'>
                    <span className='text-sm font-semibold text-white'>{slot.label}</span>
                    {slot.required ? (
                      <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-[#F42026]/70 text-white'>Required</span>
                    ) : (
                      <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white/10 text-gray-400'>Optional</span>
                    )}
                  </div>

                  {slot.file ? (
                    <div className='flex items-center gap-2'>
                      <span className='text-xs text-gray-300 font-mono truncate'>{slot.previewName}</span>
                      {slot.uploadState === 'done' && (
                        <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-500/80 text-white'>Uploaded</span>
                      )}
                      {slot.uploadState === 'uploading' && (
                        <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/80 text-white'>Uploading…</span>
                      )}
                      {slot.uploadState === 'error' && (
                        <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-500/80 text-white'>Error</span>
                      )}
                    </div>
                  ) : (
                    <p className='text-xs text-gray-500'>No file selected</p>
                  )}

                  {slot.error && (
                    <p className='text-xs text-red-300 mt-1'>{slot.error}</p>
                  )}

                  {slot.uploadedUrl && (
                    <a
                      href={slot.uploadedUrl}
                      target='_blank'
                      rel='noreferrer'
                      className='text-[11px] text-[#FF9C15] hover:underline mt-1 block truncate'
                    >
                      {slot.uploadedUrl}
                    </a>
                  )}
                </div>

                <label className={`shrink-0 cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                  submitting ? 'opacity-50 pointer-events-none' : 'bg-white/10 text-white hover:bg-white/20'
                }`}>
                  {slot.file ? 'Replace' : 'Select file'}
                  <input
                    type='file'
                    accept={ACCEPTED}
                    className='hidden'
                    onChange={(e) => handleFileSelect(slot.assetType, e)}
                    disabled={submitting}
                  />
                </label>
              </div>

              {slot.previewData && (
                <div className='w-full flex items-center justify-center rounded-lg overflow-hidden bg-black/30 border border-white/10' style={{ height: 160 }}>
                  <LottiePreview data={slot.previewData} />
                </div>
              )}
            </div>
          ))}

          {/* Audio slot */}
          <div className={`border rounded-xl p-4 transition space-y-3 ${
            audioSlot.uploadState === 'done' ? 'border-emerald-400/40 bg-emerald-500/5' :
            audioSlot.uploadState === 'error' ? 'border-red-400/40 bg-red-500/5' :
            audioSlot.uploadState === 'uploading' ? 'border-amber-400/40 bg-amber-500/5' :
            'border-white/10 bg-white/5'
          }`}>
            <div className='flex items-start justify-between gap-3'>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2 mb-1'>
                  <span className='text-sm font-semibold text-white'>Sound Effect (Audio)</span>
                  <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white/10 text-gray-400'>Optional</span>
                </div>
                {audioSlot.file ? (
                  <div className='flex items-center gap-2'>
                    <span className='text-xs text-gray-300 font-mono truncate'>{audioSlot.file.name}</span>
                    {audioSlot.uploadState === 'done' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-500/80 text-white'>Uploaded</span>}
                    {audioSlot.uploadState === 'uploading' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/80 text-white'>Uploading…</span>}
                    {audioSlot.uploadState === 'error' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-500/80 text-white'>Error</span>}
                  </div>
                ) : (
                  <p className='text-xs text-gray-500'>No file selected</p>
                )}
                {audioSlot.error && <p className='text-xs text-red-300 mt-1'>{audioSlot.error}</p>}
                {audioSlot.uploadedUrl && (
                  <a href={audioSlot.uploadedUrl} target='_blank' rel='noreferrer' className='text-[11px] text-[#FF9C15] hover:underline mt-1 block truncate'>
                    {audioSlot.uploadedUrl}
                  </a>
                )}
              </div>
              <label className={`shrink-0 cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                submitting ? 'opacity-50 pointer-events-none' : 'bg-white/10 text-white hover:bg-white/20'
              }`}>
                {audioSlot.file ? 'Replace' : 'Select file'}
                <input
                  type='file'
                  accept='audio/mpeg,audio/aac,audio/wav,audio/mp4,.mp3,.aac,.wav,.m4a'
                  className='hidden'
                  onChange={handleAudioSelect}
                  disabled={submitting}
                />
              </label>
            </div>
          </div>

          {/* WebM slot */}
          <div className={`border rounded-xl p-4 transition space-y-3 ${
            webmSlot.uploadState === 'done' ? 'border-emerald-400/40 bg-emerald-500/5' :
            webmSlot.uploadState === 'error' ? 'border-red-400/40 bg-red-500/5' :
            webmSlot.uploadState === 'uploading' ? 'border-amber-400/40 bg-amber-500/5' :
            'border-white/10 bg-white/5'
          }`}>
            <div className='flex items-start justify-between gap-3'>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2 mb-1'>
                  <span className='text-sm font-semibold text-white'>WebM Animation (Video)</span>
                  <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white/10 text-gray-400'>Optional</span>
                </div>
                {webmSlot.file ? (
                  <div className='flex items-center gap-2'>
                    <span className='text-xs text-gray-300 font-mono truncate'>{webmSlot.file.name}</span>
                    {webmSlot.uploadState === 'done' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-500/80 text-white'>Uploaded</span>}
                    {webmSlot.uploadState === 'uploading' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/80 text-white'>Uploading…</span>}
                    {webmSlot.uploadState === 'error' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-500/80 text-white'>Error</span>}
                  </div>
                ) : (
                  <p className='text-xs text-gray-500'>No file selected</p>
                )}
                {webmSlot.error && <p className='text-xs text-red-300 mt-1'>{webmSlot.error}</p>}
                {webmSlot.uploadedUrl && (
                  <a href={webmSlot.uploadedUrl} target='_blank' rel='noreferrer' className='text-[11px] text-[#FF9C15] hover:underline mt-1 block truncate'>
                    {webmSlot.uploadedUrl}
                  </a>
                )}
              </div>
              <label className={`shrink-0 cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                submitting ? 'opacity-50 pointer-events-none' : 'bg-white/10 text-white hover:bg-white/20'
              }`}>
                {webmSlot.file ? 'Replace' : 'Select file'}
                <input
                  type='file'
                  accept='video/webm,.webm'
                  className='hidden'
                  onChange={handleWebmSelect}
                  disabled={submitting}
                />
              </label>
            </div>
          </div>

          {/* iOS Alpha (MOV) slot */}
          <div className={`border rounded-xl p-4 transition space-y-3 ${
            movSlot.uploadState === 'done' ? 'border-emerald-400/40 bg-emerald-500/5' :
            movSlot.uploadState === 'error' ? 'border-red-400/40 bg-red-500/5' :
            movSlot.uploadState === 'uploading' ? 'border-amber-400/40 bg-amber-500/5' :
            'border-white/10 bg-white/5'
          }`}>
            <div className='flex items-start justify-between gap-3'>
              <div className='flex-1 min-w-0'>
                <div className='flex items-center gap-2 mb-1'>
                  <span className='text-sm font-semibold text-white'>iOS Animation (HEVC Alpha MOV/MP4)</span>
                  <span className='px-1.5 py-0.5 text-[10px] font-semibold rounded bg-white/10 text-gray-400'>Optional</span>
                </div>
                {movSlot.file ? (
                  <div className='flex items-center gap-2'>
                    <span className='text-xs text-gray-300 font-mono truncate'>{movSlot.file.name}</span>
                    {movSlot.uploadState === 'done' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-emerald-500/80 text-white'>Uploaded</span>}
                    {movSlot.uploadState === 'uploading' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-500/80 text-white'>Uploading…</span>}
                    {movSlot.uploadState === 'error' && <span className='shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded bg-red-500/80 text-white'>Error</span>}
                  </div>
                ) : (
                  <p className='text-xs text-gray-500'>No file selected</p>
                )}
                {movSlot.error && <p className='text-xs text-red-300 mt-1'>{movSlot.error}</p>}
                {movSlot.uploadedUrl && (
                  <a href={movSlot.uploadedUrl} target='_blank' rel='noreferrer' className='text-[11px] text-[#FF9C15] hover:underline mt-1 block truncate'>
                    {movSlot.uploadedUrl}
                  </a>
                )}
              </div>
              <label className={`shrink-0 cursor-pointer px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                submitting ? 'opacity-50 pointer-events-none' : 'bg-white/10 text-white hover:bg-white/20'
              }`}>
                {movSlot.file ? 'Replace' : 'Select file'}
                <input
                  type='file'
                  accept='video/quicktime,video/mp4,video/x-m4v,.mov,.mp4,.m4v'
                  className='hidden'
                  onChange={handleMovSelect}
                  disabled={submitting}
                />
              </label>
            </div>
          </div>

          <div className='text-xs text-gray-500 pt-1'>
            <span className='font-semibold text-gray-400'>animation</span> → Lottie JSON for the gift grid thumbnail and fallback overlay<br />
            <span className='font-semibold text-gray-400'>webm</span> → HEVC alpha WebM video for Android livestream overlay (VP9 + alpha, optional)<br />
            <span className='font-semibold text-gray-400'>ios_alpha</span> → HEVC alpha MOV/MP4 for iOS livestream overlay (transparent background + audio, optional)<br />
            <span className='font-semibold text-gray-400'>preview</span> → lightweight Lottie shown as a static first-frame thumbnail in the gift grid (optional)<br />
            <span className='font-semibold text-gray-400'>background</span> → full-screen overlay behind the gift (optional)<br />
            <span className='font-semibold text-gray-400'>audio</span> → sound effect played on the livestream when the gift fires (mp3/aac/wav, optional)
          </div>
        </div>

        <div className='flex items-center justify-between bg-white/5 border border-white/10 rounded-2xl px-6 py-4'>
          <p className='text-xs text-gray-400'>
            {!giftSlot.file ? 'Add a gift animation to continue.' : `Ready — ${[giftSlot, previewSlot, bgSlot].filter(s => s.file).length + (audioSlot.file ? 1 : 0)} file(s) selected.`}
          </p>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className='px-6 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-50'
          >
            {submitting ? 'Creating…' : 'Create Event Gift'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateEventGift;
